import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Card } from '@clari/ui'
import { AuthLayout } from '@/components/AuthLayout'
import { authApi } from '@/lib/auth-api'
import { useAuthStore } from '@/stores/authStore'

type State = 'loading' | 'success' | 'error'

export function VerifyEmail() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token')
  const setUser = useAuthStore((s) => s.setUser)
  const isAuthed = useAuthStore((s) => s.accessToken !== null)
  const [state, setState] = useState<State>('loading')
  const destination = isAuthed ? '/app' : '/login'
  // The verification token is single-use; guard against double invocation
  // (StrictMode / re-render) which would 401 on the second call and flip the
  // UI to the error state after the first call already succeeded.
  const startedRef = useRef(false)

  useEffect(() => {
    if (!token) {
      setState('error')
      return
    }
    if (startedRef.current) return
    startedRef.current = true
    authApi
      .verifyEmail(token)
      .then((res) => {
        setUser(res.user)
        setState('success')
      })
      .catch(() => {
        setState('error')
      })
  }, [token, setUser])

  return (
    <AuthLayout>
      <Card>
        {state === 'loading' ? (
          <p className="text-text-muted">{t('verify_email.loading')}</p>
        ) : state === 'success' ? (
          <>
            <h1 className="text-2xl font-semibold">{t('verify_email.success_title')}</h1>
            <p className="mt-1 text-text-muted">{t('verify_email.success_body')}</p>
            <Button className="mt-6" onClick={() => navigate(destination, { replace: true })}>
              {t('verify_email.continue')}
            </Button>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold">{t('verify_email.error_title')}</h1>
            <p className="mt-1 text-text-muted">{t('verify_email.error_body')}</p>
            <Button
              variant="secondary"
              className="mt-6"
              onClick={() => navigate(destination, { replace: true })}
            >
              {t('actions.continue')}
            </Button>
          </>
        )}
      </Card>
    </AuthLayout>
  )
}
